import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { STAGE_DOT } from "@/pages/Quotes";

export default function QuoteStages() {
  const nav = useNavigate();
  const [stages, setStages] = useState([]);
  const [colors, setColors] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/settings/quote-stages");
    setStages(data.stages);
    setColors(data.colors);
    setCounts(data.counts || {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (i, patch) =>
    setStages((cur) => cur.map((s, n) => (n === i ? { ...s, ...patch } : s)));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/quote-stages", { stages });
      setStages(data.stages);
      setCounts(data.counts || {});
      toast.success("Quote stages saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save the stages",
      );
    }
    setBusy(false);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Quote stages" />
      <main className="relative z-10 pb-36 pt-6">
        <button
          type="button"
          data-testid="stages-back"
          onClick={() => nav("/settings")}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Settings
        </button>

        <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Quote stages
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            The stages a quote can sit in. Rename them, pick a color, add your
            own or remove ones you never use.
          </p>
        </div>

        <div className="mt-6 space-y-3" data-testid="stage-rows">
          {stages.map((s, i) => (
            <div
              key={i}
              data-testid={`stage-row-${i}`}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-3"
            >
              <span
                className={`h-8 w-1.5 shrink-0 rounded-full ${STAGE_DOT[s.color] || STAGE_DOT.zinc}`}
              />
              <Input
                data-testid={`stage-name-${i}`}
                value={s.name}
                onChange={(e) => set(i, { name: e.target.value })}
                placeholder="Stage name"
                className="h-11 w-52 border-zinc-800 bg-zinc-950 text-sm"
              />
              <Select
                value={s.color || "zinc"}
                onValueChange={(v) => set(i, { color: v })}
              >
                <SelectTrigger
                  data-testid={`stage-color-${i}`}
                  className="h-11 w-36 border-zinc-800 bg-zinc-950 text-sm capitalize"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colors.map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="font-mono-data text-[11px] text-zinc-500">
                {counts[s.name] || 0} quote(s)
              </span>
              <Button
                data-testid={`stage-remove-${i}`}
                variant="outline"
                onClick={() =>
                  setStages((cur) => cur.filter((_, n) => n !== i))
                }
                className="ml-auto h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest text-red-400 hover:bg-zinc-800"
              >
                <Trash size={14} weight="bold" />
              </Button>
            </div>
          ))}
          <button
            type="button"
            data-testid="stage-add"
            onClick={() =>
              setStages((cur) => [...cur, { name: "", color: "violet" }])
            }
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-zinc-700 py-4 text-xs font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-400/50 hover:text-indigo-200"
          >
            <Plus size={16} weight="bold" /> Add stage
          </button>
        </div>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-zinc-800 bg-zinc-950/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-6xl justify-end gap-2">
          <Button
            data-testid="stages-cancel"
            variant="outline"
            onClick={load}
            className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            data-testid="stages-save"
            disabled={busy}
            onClick={save}
            className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
